// src/services/secureProxy.ts
/**
 * Couche client sécurisée pour Forfeo Explore.
 *
 * Objectif : empêcher les appels directs depuis le navigateur vers des APIs privées
 * comme OpenAI, Amadeus, Shippo, etc. Les vraies clés doivent rester côté backend,
 * Supabase Edge Functions ou Render backend.
 */

type JsonRecord = Record<string, any>;

type ProxyRequestOptions = {
  timeoutMs?: number;
  headers?: Record<string, string>;
};

export type OpenAIChatMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string;
};

export type OpenAIChatRequest = {
  model?: string;
  messages: OpenAIChatMessage[];
  temperature?: number;
  max_tokens?: number;
  response_format?: JsonRecord;
};

const getEnv = (key: string): string => {
  const env = (import.meta as any)?.env || {};
  return String(env[key] || '').trim();
};

export const OPENAI_PROXY_URL = getEnv('VITE_OPENAI_PROXY_URL');
export const AMADEUS_PROXY_URL = getEnv('VITE_AMADEUS_PROXY_URL');
export const SHIPPO_PROXY_URL = getEnv('VITE_SHIPPO_PROXY_URL');

const normalizeBaseUrl = (url: string) => url.replace(/\/+$/, '');

const joinUrl = (base: string, path: string) => {
  const cleanBase = normalizeBaseUrl(base);
  const cleanPath = path.replace(/^\/+/, '');
  return `${cleanBase}/${cleanPath}`;
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs = 30000): Promise<T> => {
  let timer: number | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => {
      reject(new Error(`Délai d'attente dépassé après ${timeoutMs} ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) window.clearTimeout(timer);
  }
};

const parseJsonResponse = async (response: Response) => {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (error) {
    return { raw: text };
  }
};

export const postJson = async <T = any>(
  url: string,
  body: JsonRecord,
  options: ProxyRequestOptions = {}
): Promise<T> => {
  const request = fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    body: JSON.stringify(body),
  }).then(async (response) => {
    const data = await parseJsonResponse(response);

    if (!response.ok) {
      const message = data?.error?.message || data?.message || `Erreur proxy ${response.status}`;
      throw new Error(message);
    }

    return data as T;
  });

  return withTimeout(request, options.timeoutMs);
};

export const postFormData = async <T = any>(
  url: string,
  body: FormData,
  options: ProxyRequestOptions = {}
): Promise<T> => {
  const request = fetch(url, {
    method: 'POST',
    headers: options.headers || {},
    body,
  }).then(async (response) => {
    const data = await parseJsonResponse(response);

    if (!response.ok) {
      const message = data?.error?.message || data?.message || `Erreur proxy ${response.status}`;
      throw new Error(message);
    }

    return data as T;
  });

  return withTimeout(request, options.timeoutMs);
};

const getOpenAICandidates = (baseUrl: string, endpoint: string) => {
  const base = normalizeBaseUrl(baseUrl);
  const cleanEndpoint = endpoint.replace(/^\/+/, '');

  return Array.from(
    new Set([
      base,
      joinUrl(base, cleanEndpoint),
      joinUrl(base, `v1/${cleanEndpoint}`),
      joinUrl(base, `openai/${cleanEndpoint}`),
    ])
  );
};

export const callOpenAIChatCompletion = async (payload: OpenAIChatRequest) => {
  if (!OPENAI_PROXY_URL) {
    throw new Error('VITE_OPENAI_PROXY_URL est manquant. Les appels OpenAI directs côté navigateur sont désactivés pour des raisons de sécurité.');
  }

  const body = {
    model: payload.model || 'gpt-4o-mini',
    messages: payload.messages,
    temperature: payload.temperature ?? 0.7,
    ...(typeof payload.max_tokens === 'number' ? { max_tokens: payload.max_tokens } : {}),
    ...(payload.response_format ? { response_format: payload.response_format } : {}),
  };

  let lastError: unknown = null;

  for (const url of getOpenAICandidates(OPENAI_PROXY_URL, 'chat/completions')) {
    try {
      return await postJson(url, body);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Impossible de joindre le proxy OpenAI.');
};

export const transcribeAudioViaProxy = async (audioBlob: Blob, language = 'fr') => {
  if (!OPENAI_PROXY_URL) {
    throw new Error('VITE_OPENAI_PROXY_URL est manquant. La transcription Whisper directe côté navigateur est désactivée.');
  }

  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.wav');
  formData.append('model', 'whisper-1');
  formData.append('language', language);

  let lastError: unknown = null;

  for (const url of getOpenAICandidates(OPENAI_PROXY_URL, 'audio/transcriptions')) {
    try {
      return await postFormData(url, formData);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Impossible de joindre le proxy de transcription.');
};

export const callAmadeusProxy = async <T = any>(
  endpoint: string,
  params: Record<string, string | number | boolean | undefined | null> = {},
  options: ProxyRequestOptions = {}
): Promise<T> => {
  if (!AMADEUS_PROXY_URL) {
    throw new Error('VITE_AMADEUS_PROXY_URL est manquant. Les appels Amadeus directs côté navigateur sont désactivés pour des raisons de sécurité.');
  }

  const base = normalizeBaseUrl(AMADEUS_PROXY_URL);
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      searchParams.set(key, String(value));
    }
  });

  const qs = searchParams.toString();
  const candidates = Array.from(
    new Set([
      `${base}${qs ? `?${qs}&` : '?'}endpoint=${encodeURIComponent(cleanEndpoint)}`,
      `${joinUrl(base, cleanEndpoint)}${qs ? `?${qs}` : ''}`,
      `${joinUrl(base, `v1/${cleanEndpoint}`)}${qs ? `?${qs}` : ''}`,
      `${joinUrl(base, `v2/${cleanEndpoint.replace(/^v2\//, '')}`)}${qs ? `?${qs}` : ''}`,
    ])
  );

  let lastError: unknown = null;

  for (const url of candidates) {
    try {
      const request = fetch(url, {
        method: 'GET',
        headers: options.headers || {},
      }).then(async (response) => {
        const data = await parseJsonResponse(response);
        if (!response.ok) {
          const message = data?.error?.message || data?.message || `Erreur proxy Amadeus ${response.status}`;
          throw new Error(message);
        }
        return data as T;
      });

      return await withTimeout(request, options.timeoutMs);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Impossible de joindre le proxy Amadeus.');
};
